import { useEffect, useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Search, User, ListChecks, Calendar, FileText, StickyNote, ClipboardList } from "lucide-react";

export default function GlobalSearch() {
  const { currentOrgId, user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim() || !currentOrgId) { setResults([]); return; }
    const term = q.trim();
    const like = `%${term}%`;
    const t = setTimeout(async () => {
      const [c, a, k, d, n, i] = await Promise.all([
        supabase.from("clients").select("id,first_name,last_name,email").eq("organization_id", currentOrgId).or(`first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like},phone.ilike.${like}`).limit(5),
        supabase.from("appointments").select("id,title,starts_at,client_id").eq("organization_id", currentOrgId).ilike("title", like).limit(5),
        supabase.from("tasks").select("id,title,client_id").eq("organization_id", currentOrgId).ilike("title", like).limit(5),
        supabase.from("documents").select("id,name,client_id").eq("organization_id", currentOrgId).ilike("name", like).limit(5),
        supabase.from("notes").select("id,body,client_id").eq("organization_id", currentOrgId).ilike("body", like).limit(5),
        supabase.from("intake_records").select("id,client_id,clients(first_name,last_name)").eq("organization_id", currentOrgId).limit(5),
      ]);
      const rows = [
        ...(c.data || []).map((r) => ({ id: `c-${r.id}`, kind: "Client", icon: User, label: `${r.first_name} ${r.last_name}`, sub: r.email || "", link: `/app/clients/${r.id}` })),
        ...(a.data || []).map((r) => ({ id: `a-${r.id}`, kind: "Appointment", icon: Calendar, label: r.title, sub: new Date(r.starts_at).toLocaleString(), link: `/app/calendar` })),
        ...(k.data || []).map((r) => ({ id: `k-${r.id}`, kind: "Task", icon: ListChecks, label: r.title, sub: "", link: `/app/tasks` })),
        ...(d.data || []).map((r) => ({ id: `d-${r.id}`, kind: "Document", icon: FileText, label: r.name, sub: "", link: r.client_id ? `/app/clients/${r.client_id}` : `/app/documents` })),
        ...(n.data || []).map((r) => ({ id: `n-${r.id}`, kind: "Note", icon: StickyNote, label: r.body.slice(0, 80), sub: "", link: `/app/clients/${r.client_id}` })),
      ];
      setResults(rows);
    }, 200);
    return () => clearTimeout(t);
  }, [q, currentOrgId]);

  const go = (link) => { setOpen(false); setQ(""); navigate(link); };

  return (
    <div className="relative flex-1 max-w-md" ref={boxRef}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E] pointer-events-none" />
      <input
        data-testid="global-search"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Search clients, tasks, appointments…"
        className="w-full pl-9 pr-3 py-2 bg-white border border-[#E8E2D9] rounded-full text-sm placeholder:text-[#A8A29E] focus:outline-none focus:border-[#E8712C]"
      />
      {open && q.trim() && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#E8E2D9] rounded-xl shadow-xl z-30 overflow-hidden max-h-[420px] overflow-y-auto">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#A8A29E]">No results</div>
          ) : (
            <ul className="divide-y divide-[#E8E2D9]">
              {results.map((r) => (
                <li key={r.id}>
                  <button data-testid={`search-result-${r.id}`} onClick={() => go(r.link)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#FAF8F5] text-left">
                    <div className="w-8 h-8 rounded-lg bg-[#FDE9D8] flex items-center justify-center flex-shrink-0"><r.icon size={14} className="text-[#E8712C]" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.label}</div>
                      {r.sub && <div className="text-xs text-[#57534E] truncate">{r.sub}</div>}
                    </div>
                    <div className="text-[10px] text-[#A8A29E] uppercase tracking-widest">{r.kind}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
